'use client'

import {
  PersonListCard,
  Badge,
  Button,
  ListCardTrailingActionsFooter,
  applicantCardToPersonModel,
  jobProspectMatchToPersonModel,
  gapClosingSkillKeys,
  prospectHasGapClosingSkill,
  displayableAnalysisHeadline,
  MatchQualityBadge,
  formatRelativeTimeIso,
  calendarDaysSinceIso,
} from '@yeshuman/sdk'
import { Bell, Mail } from 'lucide-react'

import { InviteToApplyAction } from './invite-to-apply-action'
import type { ApplicantCard } from '../../hooks/use-opportunity-applicants'
import type { JobProspectMatch } from '../../hooks/use-job-prospect-matches'
import type { EmailInviteSummary } from '../../hooks/use-opportunity-email-invites'
import type { JobAnalysisCoverage } from '../../hooks/use-job-analysis-coverage'

export type SourcingRowVariant = 'email-pending' | 'invited-pending' | 'suggestion'

type EmailPendingProps = {
  variant: 'email-pending'
  invite: EmailInviteSummary
  onResend: (inviteId: string) => void
  onRevoke: (inviteId: string) => void
  resendPending?: boolean
  revokePending?: boolean
}

type InvitedPendingProps = {
  variant: 'invited-pending'
  applicant: ApplicantCard
  onNudge: (applicationId: string) => void
  onOpen?: () => void
  nudgePending?: boolean
}

type SuggestionProps = {
  variant: 'suggestion'
  match: JobProspectMatch
  coverage?: JobAnalysisCoverage | null
  onOpen?: () => void
  onInvite: (profileId: string) => void
  isApplied: boolean
  isInvited: boolean
  isInviting: boolean
}

export type SourcingRowProps = EmailPendingProps | InvitedPendingProps | SuggestionProps

export function SourcingRow(props: SourcingRowProps) {
  if (props.variant === 'email-pending') {
    const days = calendarDaysSinceIso(props.invite.created_at)
    const accountSuffix = props.invite.has_account ? 'opened' : 'no account'
    return (
      <article className="rounded-lg border border-dashed border-border/80 bg-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted">
              <Mail className="size-4 text-muted-foreground" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="truncate font-medium text-foreground">{props.invite.email}</p>
              <Badge variant="secondarySoft" className="font-normal">
                Invite sent · {days}d · {accountSuffix}
              </Badge>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={props.resendPending || props.invite.resend_count >= 1}
              onClick={() => props.onResend(props.invite.id)}
            >
              Resend
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={props.revokePending}
              onClick={() => props.onRevoke(props.invite.id)}
            >
              Revoke
            </Button>
          </div>
        </div>
      </article>
    )
  }

  if (props.variant === 'invited-pending') {
    const applicant = props.applicant
    const days = calendarDaysSinceIso(applicant.applied_at)
    const nudgedLabel = applicant.nudged_at
      ? `Nudged ${formatRelativeTimeIso(applicant.nudged_at)}`
      : null
    const skills = applicant.profile.top_skills?.slice(0, 5) ?? []

    return (
      <PersonListCard
        variant="standard"
        className="border-dashed"
        person={applicantCardToPersonModel(applicant)}
        onOpen={props.onOpen}
        openAriaLabel={
          props.onOpen ? `Open candidate for ${applicant.profile.name}` : undefined
        }
        headerAside={
          typeof applicant.match_score === 'number' ? (
            <MatchQualityBadge
              score={applicant.match_score}
              percentSuffix=""
              className="shrink-0"
            />
          ) : null
        }
        body={
          <div className="space-y-2">
            <Badge variant="warningSoft">
              Invited · {days}d · awaiting application
            </Badge>
            {skills.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {skills.map((skill) => (
                  <Badge key={skill} variant="secondarySoft" className="font-normal">
                    {skill}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        }
        footer={
          <ListCardTrailingActionsFooter>
            {nudgedLabel ? (
              <span className="text-sm text-muted-foreground">{nudgedLabel}</span>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={props.nudgePending}
                onClick={() => props.onNudge(applicant.id)}
              >
                <Bell className="mr-1.5 shrink-0" aria-hidden />
                Nudge
              </Button>
            )}
          </ListCardTrailingActionsFooter>
        }
        footerClassName="pointer-events-auto px-0"
      />
    )
  }

  const match = props.match
  const gapKeys = gapClosingSkillKeys(props.coverage)
  const analysisHeadline = displayableAnalysisHeadline(match.analysis_headline)

  return (
    <PersonListCard
      variant="standard"
      className="border-dashed"
      person={jobProspectMatchToPersonModel(match)}
      onOpen={props.onOpen}
      openAriaLabel={props.onOpen ? `Open candidate for ${match.name}` : undefined}
      headerAside={
        <MatchQualityBadge
          score={match.match_percent / 100}
          estimate
          percentSuffix="est."
          className="shrink-0"
        />
      }
      body={
        <div className="space-y-2">
          {analysisHeadline ? (
            <p className="text-sm font-medium">{analysisHeadline}</p>
          ) : null}
          {match.top_skills?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {match.top_skills.slice(0, 5).map((skill) => {
                const isGapCloser = prospectHasGapClosingSkill(
                  { ...match, top_skills: [skill] },
                  gapKeys,
                )
                return (
                  <Badge
                    key={skill}
                    variant={isGapCloser ? 'warningSoft' : 'secondarySoft'}
                    className="font-normal"
                  >
                    {skill}
                  </Badge>
                )
              })}
            </div>
          ) : null}
        </div>
      }
      footer={
        <ListCardTrailingActionsFooter>
          <InviteToApplyAction
            profileId={match.profile_id}
            isApplied={props.isApplied}
            isInvited={props.isInvited}
            isInviting={props.isInviting}
            onInvite={props.onInvite}
          />
        </ListCardTrailingActionsFooter>
      }
      footerClassName="pointer-events-auto px-0"
    />
  )
}
